import { useMemo } from "react";
import { find, map } from "lodash";
import {
  useDeleteProjectNoteMutation,
  useProjectNotesQuery,
  useUpsertProjectNoteMutation,
  ProjectNotesDocument,
  UpsertProjectNoteMutationVariables,
  ProjectNotesQuery,
  ProjectNote,
} from "@/features/projects/graphql/generated";
import { useAccountMembers } from "@/features/workspace/hooks/useAccountMembers";

export const useProjectNotes = (
  projectId: number,
  personId: number,
  type?: string
) => {
  const { data, loading, error } = useProjectNotesQuery({
    variables: {
      projectId,
      personId,
      type,
    },
  });

  const { accountMembers: people } = useAccountMembers();

  const notes = useMemo(
    () =>
      map(data?.projectNotes, (note) => ({
        ...note,
        createdBy: find(people, { id: note?.created_by }),
      })),
    [data, people]
  );

  const [createProjectNote, { loading: creating }] =
    useUpsertProjectNoteMutation({ refetchQueries: ["ProjectNotes"] });
  const [deleteProjectNote, { loading: deleting }] =
    useDeleteProjectNoteMutation({ refetchQueries: ["ProjectNotes"] });

  const handleupsertProjectNote = async (
    input: UpsertProjectNoteMutationVariables
  ) => {
    await createProjectNote({
      variables: {
        ...input,
        type: input.type || type || "note",
        projectId,
        personId,
      },
      update: (cache, { data }) => {
        if (!data?.upsertProjectNote) return;

        // If this is an update (has ID), update the cache
        if (input.id) {
          const existingNotes = cache.readQuery<ProjectNotesQuery>({
            query: ProjectNotesDocument,
            variables: {
              projectId,
              personId,
              type: type || "note",
            },
          });

          if (existingNotes?.projectNotes) {
            cache.writeQuery({
              query: ProjectNotesDocument,
              variables: {
                projectId,
                personId,
                type: type || "note",
              },
              data: {
                projectNotes: existingNotes.projectNotes.map(
                  (note: ProjectNote | null | undefined) =>
                    note?.id === input.id ? data.upsertProjectNote : note
                ),
              },
            });
          }
        } else {
          // For new notes, refetch the query
          cache.evict({ fieldName: "projectNotes" });
          cache.gc();
        }
      },
    });
  };

  const handleDeleteProjectNote = async (id: number) => {
    await deleteProjectNote({
      variables: {
        id,
      },
      refetchQueries: ["ProjectNotes"],
    });
  };

  return {
    notes,
    loading: loading || creating,
    deleting,
    handleupsertProjectNote,
    handleDeleteProjectNote,
  };
};
